import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Plus, Trash2, Tag, Search, FileText, BookOpen, Link2,
  ChevronRight, ChevronLeft, Pencil, Sparkles, Check, X, ExternalLink, Type,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function InlineEditPill({ tag, onSave, isSelected, onClick }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(tag.name);
  const inputRef = useRef(null);

  const commit = () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== tag.name) {
      onSave(tag.id, trimmed);
    } else {
      setValue(tag.name);
    }
    setEditing(false);
  };

  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  if (editing) {
    return (
      <span className="inline-flex items-center rounded-full border px-1 py-0.5" style={{ borderColor: "var(--primary-color)", backgroundColor: "color-mix(in srgb, var(--primary-color) 10%, transparent)" }}>
        <input ref={inputRef} value={value} onChange={(e) => setValue(e.target.value)}
          onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setValue(tag.name); setEditing(false); } }}
          className="bg-transparent border-none outline-none text-sm font-body px-2 py-0.5 w-auto min-w-[60px]"
          style={{ color: "var(--admin-container-text)", width: `${Math.max(60, value.length * 8)}px` }}
          data-testid={`metatag-inline-edit-${tag.id}`}
        />
      </span>
    );
  }

  return (
    <button onClick={onClick} onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-body font-medium transition-all cursor-pointer border ${isSelected ? "ring-2" : ""}`}
      style={{
        backgroundColor: isSelected ? "color-mix(in srgb, var(--primary-color) 20%, transparent)" : "color-mix(in srgb, var(--primary-color) 8%, transparent)",
        borderColor: isSelected ? "var(--primary-color)" : "transparent",
        color: "var(--admin-container-text)",
        ringColor: "var(--primary-color)",
      }}
      data-testid={`metatag-pill-${tag.id}`}
    >
      <Tag className="h-3 w-3 flex-shrink-0" style={{ color: "var(--primary-color)" }} />
      {tag.name}
      {tag.rules_count > 0 && (
        <span className="text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-none"
          style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
          data-testid={`metatag-rules-badge-${tag.id}`}>
          {tag.rules_count}
        </span>
      )}
    </button>
  );
}

const TYPE_LABELS = { mc: "Multiple Choice", sata: "Select All", task: "Task", likert: "Likert", external_score: "External Score" };

// ───────────────────────── Rule Creator Wizard ─────────────────────────
function RuleCreatorWizard({ open, onClose, editingRule, tags, resources, onSaved, onTagCreated }) {
  const [step, setStep] = useState(1);
  const [tagId, setTagId] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [creatingTag, setCreatingTag] = useState(false);
  const [responses, setResponses] = useState([]);
  const [newResponseType, setNewResponseType] = useState("text");
  const [newTextContent, setNewTextContent] = useState("");
  const [newResourceId, setNewResourceId] = useState("");
  const [saving, setSaving] = useState(false);

  const filteredTags = useMemo(() => {
    const q = tagSearch.toLowerCase().trim();
    return q ? tags.filter((t) => t.name.toLowerCase().includes(q)) : tags;
  }, [tags, tagSearch]);

  const selectedTag = tags.find((t) => t.id === tagId);

  useEffect(() => {
    if (open && editingRule) {
      setStep(1);
      setTagId(editingRule.tag_id || "");
      setResponses((editingRule.responses || []).map((r) => ({ ...r })));
    } else if (open) {
      setStep(1);
      setTagId("");
      setTagSearch("");
      setNewTagName("");
      setResponses([]);
      setNewResponseType("text");
      setNewTextContent("");
      setNewResourceId("");
    }
  }, [open, editingRule]);

  const handleCreateTagInline = async () => {
    const name = newTagName.trim();
    if (!name) return;
    setCreatingTag(true);
    try {
      const { data } = await axios.post(`${API}/meta-tags`, { name, description: "" }, { withCredentials: true });
      toast.success("Tag created");
      if (onTagCreated) onTagCreated();
      setTagId(data.id);
      setTagSearch(data.name);
      setShowTagDropdown(false);
      setNewTagName("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create tag");
    } finally {
      setCreatingTag(false);
    }
  };

  const addResponse = () => {
    if (newResponseType === "text") {
      const content = newTextContent.trim();
      if (!content) { toast.error("Please enter some text"); return; }
      setResponses((prev) => [...prev, { type: "text", content }]);
      setNewTextContent("");
    } else {
      if (!newResourceId) { toast.error("Please select a resource"); return; }
      const res = resources.find((r) => r.id === newResourceId);
      if (!res) return;
      setResponses((prev) => [...prev, {
        type: "resource", resource_id: res.id, resource_title: res.title, resource_url: res.url,
      }]);
      setNewResourceId("");
    }
  };

  const removeResponse = (idx) => {
    setResponses((prev) => prev.filter((_, i) => i !== idx));
  };

  const canProceedFromStep1 = !!tagId;
  const canProceedFromStep2 = responses.length > 0;

  const handleSave = async () => {
    if (!tagId || responses.length === 0) return;
    setSaving(true);
    try {
      const payload = { tag_id: tagId, responses };
      if (editingRule) {
        await axios.put(`${API}/logic-rules/${editingRule.id}`, payload, { withCredentials: true });
        toast.success("Rule updated");
      } else {
        await axios.post(`${API}/logic-rules`, payload, { withCredentials: true });
        toast.success("Rule created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-2xl"
        style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}
        data-testid="rule-wizard-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2" style={{ color: "var(--admin-container-text)" }}>
            <Sparkles className="h-5 w-5" style={{ color: "var(--primary-color)" }} />
            {editingRule ? "Edit Logic Rule" : "New Logic Rule"}
          </DialogTitle>
          <DialogDescription className="font-body" style={{ color: "var(--admin-container-muted)" }}>
            {step === 1 && "Step 1 of 3 — Select or create a tag"}
            {step === 2 && "Step 2 of 3 — Add responses"}
            {step === 3 && "Step 3 of 3 — Review and save"}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1.5 mt-1 mb-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex-1 h-1 rounded-full"
              style={{ backgroundColor: step >= s ? "var(--primary-color)" : "var(--admin-container-border)" }}
            />
          ))}
        </div>

        {/* Step 1: Tag selection */}
        {step === 1 && (
          <div className="space-y-4 pt-2" data-testid="rule-wizard-step-1">
            <div>
              <Label className="text-xs font-body mb-1.5 block" style={{ color: "var(--admin-container-muted)" }}>Meta Tag *</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: "var(--admin-container-muted)" }} />
                <Input
                  value={selectedTag ? selectedTag.name : tagSearch}
                  onChange={(e) => { setTagSearch(e.target.value); setTagId(""); setShowTagDropdown(true); }}
                  onFocus={() => setShowTagDropdown(true)}
                  placeholder="Search existing tags..."
                  className="pl-8 h-9 text-sm"
                  style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                  data-testid="rule-wizard-tag-search"
                />
                {showTagDropdown && (tagSearch.length > 0 || !tagId) && (
                  <div
                    className="absolute z-20 mt-1 w-full rounded-md border shadow-lg max-h-[260px] overflow-y-auto"
                    style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}
                  >
                    {filteredTags.length > 0 ? (
                      filteredTags.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => { setTagId(t.id); setTagSearch(t.name); setShowTagDropdown(false); }}
                          className="w-full text-left px-3 py-2 text-sm font-body flex items-center gap-2 hover:bg-white/[0.03]"
                          style={{ color: "var(--admin-container-text)" }}
                          data-testid={`rule-wizard-tag-opt-${t.id}`}
                        >
                          <Tag className="h-3 w-3" style={{ color: "var(--primary-color)" }} />
                          {t.name}
                          {t.has_rule && (
                            <span className="ml-auto text-[10px] font-body" style={{ color: "var(--admin-container-muted)" }}>
                              has rule
                            </span>
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>
                        No matching tags
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="pt-2 border-t" style={{ borderColor: "var(--admin-container-border)" }}>
              <Label className="text-xs font-body mb-1.5 block" style={{ color: "var(--admin-container-muted)" }}>
                Or create a new tag
              </Label>
              <div className="flex gap-2">
                <Input
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleCreateTagInline(); }}
                  placeholder="New tag name..."
                  className="text-sm h-9 flex-1"
                  style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                  data-testid="rule-wizard-new-tag-input"
                />
                <Button
                  size="sm"
                  onClick={handleCreateTagInline}
                  disabled={creatingTag || !newTagName.trim()}
                  className="text-xs font-body h-9"
                  style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
                  data-testid="rule-wizard-new-tag-btn"
                >
                  {creatingTag ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Responses */}
        {step === 2 && (
          <div className="space-y-4 pt-2" data-testid="rule-wizard-step-2">
            {/* Existing responses list */}
            {responses.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>
                  Added responses ({responses.length})
                </Label>
                <div className="space-y-1.5">
                  {responses.map((r, idx) => (
                    <div key={idx}
                      className="flex items-start gap-2 px-3 py-2 rounded-lg border"
                      style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)" }}
                      data-testid={`rule-wizard-response-${idx}`}
                    >
                      {r.type === "text" ? (
                        <Type className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--primary-color)" }} />
                      ) : (
                        <ExternalLink className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--primary-color)" }} />
                      )}
                      <div className="flex-1 min-w-0">
                        {r.type === "text" ? (
                          <p className="text-sm font-body whitespace-pre-wrap break-words" style={{ color: "var(--admin-container-text)" }}>
                            {r.content}
                          </p>
                        ) : (
                          <>
                            <p className="text-sm font-body font-medium truncate" style={{ color: "var(--admin-container-text)" }}>
                              {r.resource_title}
                            </p>
                            <p className="text-[11px] font-body truncate" style={{ color: "var(--admin-container-muted)" }}>
                              {r.resource_url}
                            </p>
                          </>
                        )}
                      </div>
                      <button
                        onClick={() => removeResponse(idx)}
                        className="p-1 rounded hover:bg-red-500/10 flex-shrink-0"
                        data-testid={`rule-wizard-remove-response-${idx}`}
                      >
                        <Trash2 className="h-3 w-3 text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add new response */}
            <div className="pt-3 border-t space-y-3" style={{ borderColor: "var(--admin-container-border)" }}>
              <div>
                <Label className="text-xs font-body mb-1.5 block" style={{ color: "var(--admin-container-muted)" }}>Response type</Label>
                <div className="flex gap-1 p-1 rounded-lg w-fit" style={{ backgroundColor: "var(--admin-bg)" }}>
                  {[{ k: "text", l: "Text Block", Icon: Type }, { k: "resource", l: "Resource Link", Icon: ExternalLink }].map(({ k, l, Icon }) => (
                    <button
                      key={k}
                      onClick={() => setNewResponseType(k)}
                      className="py-1.5 px-3 rounded-md text-xs font-body font-medium flex items-center gap-1.5 transition-colors"
                      style={{
                        backgroundColor: newResponseType === k ? "color-mix(in srgb, var(--primary-color) 25%, transparent)" : "transparent",
                        color: "var(--admin-container-text)",
                        opacity: newResponseType === k ? 1 : 0.55,
                      }}
                      data-testid={`rule-wizard-type-${k}`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              {newResponseType === "text" ? (
                <Textarea
                  value={newTextContent}
                  onChange={(e) => setNewTextContent(e.target.value)}
                  placeholder="Enter the text content learners will see..."
                  rows={3}
                  className="text-sm resize-none"
                  style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                  data-testid="rule-wizard-text-input"
                />
              ) : (
                <Select value={newResourceId} onValueChange={setNewResourceId}>
                  <SelectTrigger
                    className="h-9 text-sm font-body"
                    style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                    data-testid="rule-wizard-resource-select"
                  >
                    <SelectValue placeholder="Select a resource..." />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }} className="max-h-[260px]">
                    {resources.length === 0 ? (
                      <div className="px-3 py-2 text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>
                        No resources available. Add some in the Resource Library first.
                      </div>
                    ) : (
                      resources.map((r) => (
                        <SelectItem key={r.id} value={r.id} className="text-sm font-body" style={{ color: "var(--admin-container-text)" }}>
                          {r.title}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
              <Button
                size="sm"
                onClick={addResponse}
                className="text-xs font-body gap-1.5"
                style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
                data-testid="rule-wizard-add-response-btn"
              >
                <Plus className="h-3 w-3" /> Add Response
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="space-y-3 pt-2" data-testid="rule-wizard-step-3">
            <div className="rounded-lg border p-4 space-y-3" style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)" }}>
              <div className="flex items-center gap-2 pb-2 border-b" style={{ borderColor: "var(--admin-container-border)" }}>
                <Tag className="h-4 w-4" style={{ color: "var(--primary-color)" }} />
                <span className="text-sm font-body font-bold" style={{ color: "var(--admin-container-text)" }}>
                  {selectedTag?.name || "—"}
                </span>
                <span className="text-xs font-body ml-auto" style={{ color: "var(--admin-container-muted)" }}>
                  {responses.length} response{responses.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="space-y-2">
                {responses.map((r, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm font-body" style={{ color: "var(--admin-container-text)" }}>
                    <span className="text-[11px] mt-0.5" style={{ color: "var(--admin-container-muted)" }}>#{idx + 1}</span>
                    {r.type === "text" ? (
                      <div className="flex items-start gap-1.5 flex-1">
                        <Type className="h-3 w-3 mt-1 flex-shrink-0" style={{ color: "var(--primary-color)" }} />
                        <p className="whitespace-pre-wrap break-words">{r.content}</p>
                      </div>
                    ) : (
                      <div className="flex items-start gap-1.5 flex-1 min-w-0">
                        <ExternalLink className="h-3 w-3 mt-1 flex-shrink-0" style={{ color: "var(--primary-color)" }} />
                        <div className="min-w-0">
                          <p className="font-medium truncate">{r.resource_title}</p>
                          <p className="text-[11px] truncate" style={{ color: "var(--admin-container-muted)" }}>{r.resource_url}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: "var(--admin-container-border)" }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => step === 1 ? onClose() : setStep(step - 1)}
            className="text-xs font-body gap-1.5"
            style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}
            data-testid="rule-wizard-back-btn"
          >
            {step === 1 ? <X className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
            {step === 1 ? "Cancel" : "Back"}
          </Button>
          {step < 3 ? (
            <Button
              size="sm"
              onClick={() => setStep(step + 1)}
              disabled={step === 1 ? !canProceedFromStep1 : !canProceedFromStep2}
              className="text-xs font-body gap-1.5"
              style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
              data-testid="rule-wizard-next-btn"
            >
              Next <ChevronRight className="h-3 w-3" />
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || !canProceedFromStep1 || !canProceedFromStep2}
              className="text-xs font-body gap-1.5"
              style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
              data-testid="rule-wizard-save-btn"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              {editingRule ? "Update Rule" : "Save Rule"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────── Main Page ─────────────────────────
export default function LearningLogic() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);

  // Meta-tag create modal
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Rules
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleDeleteTarget, setRuleDeleteTarget] = useState(null);
  const [ruleDeleting, setRuleDeleting] = useState(false);

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/meta-tags`, { withCredentials: true });
      setTags(data);
    } catch { toast.error("Failed to load meta tags"); }
    finally { setLoading(false); }
  }, []);

  const fetchRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const { data } = await axios.get(`${API}/logic-rules`, { withCredentials: true });
      setRules(data || []);
    } catch { toast.error("Failed to load logic rules"); }
    finally { setRulesLoading(false); }
  }, []);

  const fetchResources = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/resources`, { withCredentials: true });
      setResources(data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchTags(); fetchRules(); fetchResources(); }, [fetchTags, fetchRules, fetchResources]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/meta-tags`, form, { withCredentials: true });
      toast.success("Meta tag created");
      setShowModal(false);
      setForm({ name: "", description: "" });
      fetchTags();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleInlineRename = async (tagId, newName) => {
    try {
      const existing = tags.find((t) => t.id === tagId);
      await axios.put(`${API}/meta-tags/${tagId}`, { name: newName, description: existing?.description || "" }, { withCredentials: true });
      toast.success("Tag renamed");
      fetchTags();
      fetchRules();
    } catch (err) { toast.error(err.response?.data?.detail || "Rename failed"); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/meta-tags/${deleteTarget.id}`, { withCredentials: true });
      toast.success("Meta tag deleted");
      if (selectedTag?.id === deleteTarget.id) setSelectedTag(null);
      setDeleteTarget(null);
      fetchTags();
      fetchRules();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(false); }
  };

  const handleRuleDelete = async () => {
    if (!ruleDeleteTarget) return;
    setRuleDeleting(true);
    try {
      await axios.delete(`${API}/logic-rules/${ruleDeleteTarget.id}`, { withCredentials: true });
      toast.success("Rule deleted");
      setRuleDeleteTarget(null);
      fetchRules();
      fetchTags();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
    finally { setRuleDeleting(false); }
  };

  const filtered = search.trim()
    ? tags.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
    : tags;

  const selectedData = selectedTag ? tags.find((t) => t.id === selectedTag.id) : null;

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return "—"; }
  };

  return (
    <div className="space-y-6" data-testid="learning-logic">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold" style={{ color: "var(--admin-text)" }}>Learning Logic</h1>
        <p className="text-sm font-body mt-1" style={{ color: "var(--admin-container-muted)" }}>
          {tags.length} meta tag{tags.length !== 1 ? "s" : ""} · {rules.length} logic rule{rules.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Meta Tag Builder section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-body font-bold uppercase tracking-wider" style={{ color: "var(--admin-container-muted)" }}>
            Meta Tag Builder
          </h2>
        </div>

        {/* Actions bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: "var(--admin-container-muted)" }} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tags..."
              className="pl-8 h-9 text-xs w-64"
              style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
              data-testid="metatag-search"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { setEditingRule(null); setWizardOpen(true); }} className="gap-1.5 font-body text-xs h-9"
              variant="outline"
              style={{ borderColor: "var(--primary-color)", color: "var(--primary-color)", backgroundColor: "transparent" }}
              data-testid="new-rule-btn">
              <Sparkles className="h-3.5 w-3.5" /> New Rule
            </Button>
            <Button onClick={() => setShowModal(true)} className="gap-1.5 font-body text-xs h-9"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
              data-testid="create-metatag-btn">
              <Plus className="h-3.5 w-3.5" /> Create Meta Tag
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--primary-color)" }} /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-xl border" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
            <Tag className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--admin-container-muted)" }} />
            <p className="text-sm font-body font-bold" style={{ color: "var(--admin-container-text)" }}>{search ? "No matching tags" : "No meta tags yet"}</p>
            <p className="text-xs font-body mt-1" style={{ color: "var(--admin-container-muted)" }}>{search ? "Try a different search" : "Create your first meta tag to get started"}</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 p-4 rounded-xl border min-h-[60px]"
              style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}
              data-testid="metatag-pills">
              {filtered.map((tag) => (
                <InlineEditPill key={tag.id} tag={tag}
                  isSelected={selectedTag?.id === tag.id}
                  onClick={() => setSelectedTag(selectedTag?.id === tag.id ? null : tag)}
                  onSave={handleInlineRename}
                />
              ))}
            </div>

            {/* Connections panel */}
            {selectedData && (
              <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}
                data-testid="metatag-connections">
                <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--admin-container-border)" }}>
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4" style={{ color: "var(--primary-color)" }} />
                    <h3 className="text-sm font-body font-bold" style={{ color: "var(--admin-container-text)" }}>
                      {selectedData.name}
                    </h3>
                    <span className="text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>
                      {selectedData.usage_count} connected item{selectedData.usage_count !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <button onClick={() => setDeleteTarget(selectedData)}
                    className="p-1.5 rounded-md transition-colors hover:bg-red-500/10" data-testid={`delete-metatag-${selectedData.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-400/70" />
                  </button>
                </div>
                {selectedData.description && (
                  <div className="px-4 py-2 border-b text-xs font-body" style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>
                    {selectedData.description}
                  </div>
                )}
                {(selectedData.connections || []).length === 0 ? (
                  <div className="px-4 py-6 text-center text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>
                    No items tagged yet. Assign this tag from the Question Bank.
                  </div>
                ) : (
                  <div className="divide-y" style={{ borderColor: "var(--admin-container-border)" }}>
                    {selectedData.connections.map((conn) => (
                      <div key={conn.item_id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.02] transition-colors">
                        <FileText className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "var(--admin-container-muted)" }} />
                        <span className="text-sm font-body flex-1 truncate" style={{ color: "var(--admin-container-text)" }}>{conn.title}</span>
                        <span className="text-[10px] font-body font-medium rounded px-1.5 py-0.5"
                          style={{ backgroundColor: "color-mix(in srgb, var(--primary-color) 10%, transparent)", color: "var(--primary-color)" }}>
                          {TYPE_LABELS[conn.item_type] || conn.item_type}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Logic Rules section */}
      <div className="space-y-3" data-testid="logic-rules-section">
        <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: "var(--admin-container-border)" }}>
          <h2 className="text-sm font-body font-bold uppercase tracking-wider" style={{ color: "var(--admin-container-muted)" }}>
            Logic Rules
          </h2>
          <span className="text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>
            {rules.length} rule{rules.length !== 1 ? "s" : ""}
          </span>
        </div>

        {rulesLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--primary-color)" }} /></div>
        ) : rules.length === 0 ? (
          <div className="text-center py-12 rounded-xl border" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }} data-testid="logic-rules-empty">
            <Sparkles className="h-8 w-8 mx-auto mb-2" style={{ color: "var(--admin-container-muted)" }} />
            <p className="text-sm font-body font-bold" style={{ color: "var(--admin-container-text)" }}>No logic rules yet</p>
            <p className="text-xs font-body mt-1 max-w-sm mx-auto" style={{ color: "var(--admin-container-muted)" }}>
              Create a rule to surface custom feedback when learners miss items with a given tag.
            </p>
            <Button
              onClick={() => { setEditingRule(null); setWizardOpen(true); }}
              size="sm" variant="outline"
              className="mt-3 gap-1.5 font-body text-xs"
              style={{ borderColor: "var(--primary-color)", color: "var(--primary-color)" }}
              data-testid="logic-rules-empty-cta"
            >
              <Plus className="h-3 w-3" /> Create First Rule
            </Button>
          </div>
        ) : (
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }} data-testid="logic-rules-list">
            <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-2.5 text-[10px] uppercase tracking-wider font-body border-b" style={{ color: "var(--admin-container-muted)", borderColor: "var(--admin-container-border)" }}>
              <div className="col-span-5">Tag</div>
              <div className="col-span-3">Responses</div>
              <div className="col-span-3">Last Edited</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--admin-container-border)" }}>
              {rules.map((rule) => (
                <div key={rule.id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-white/[0.02] transition-colors" data-testid={`logic-rule-row-${rule.id}`}>
                  <div className="col-span-5 flex items-center gap-2 min-w-0">
                    <Tag className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "var(--primary-color)" }} />
                    <span className="text-sm font-body font-medium truncate" style={{ color: "var(--admin-container-text)" }}>{rule.tag_name || "—"}</span>
                  </div>
                  <div className="col-span-3 text-sm font-body" style={{ color: "var(--admin-container-text)" }}>
                    {rule.response_count} response{rule.response_count !== 1 ? "s" : ""}
                  </div>
                  <div className="col-span-3 text-xs font-body" style={{ color: "var(--admin-container-muted)" }}>
                    {formatDate(rule.updated_at || rule.created_at)}
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    <button onClick={() => { setEditingRule(rule); setWizardOpen(true); }}
                      className="p-1.5 rounded hover:bg-white/[0.04]"
                      data-testid={`logic-rule-edit-${rule.id}`}>
                      <Pencil className="h-3.5 w-3.5" style={{ color: "var(--primary-color)" }} />
                    </button>
                    <button onClick={() => setRuleDeleteTarget(rule)}
                      className="p-1.5 rounded hover:bg-red-500/10"
                      data-testid={`logic-rule-delete-${rule.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400/70" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Rule Creator Wizard */}
      <RuleCreatorWizard
        open={wizardOpen}
        onClose={() => { setWizardOpen(false); setEditingRule(null); }}
        editingRule={editingRule}
        tags={tags}
        resources={resources}
        onSaved={() => { fetchRules(); fetchTags(); }}
        onTagCreated={fetchTags}
      />

      {/* Create Meta Tag Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: "var(--admin-container-text)" }}>Create Meta Tag</DialogTitle>
            <DialogDescription className="font-body" style={{ color: "var(--admin-container-muted)" }}>Add a new meta tag to your library.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-body mb-1.5 block" style={{ color: "var(--admin-container-muted)" }}>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Clinical Reasoning"
                className="text-sm" style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                data-testid="metatag-name-input" />
            </div>
            <div>
              <Label className="text-xs font-body mb-1.5 block" style={{ color: "var(--admin-container-muted)" }}>Description (optional)</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Brief description..."
                rows={3} className="text-sm resize-none" style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
                data-testid="metatag-desc-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowModal(false)} className="text-xs font-body"
                style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} disabled={saving} className="text-xs font-body gap-1.5"
                style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
                data-testid="metatag-save-btn">
                {saving && <Loader2 className="h-3 w-3 animate-spin" />} Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Meta Tag Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: "var(--admin-container-text)" }}>Delete Meta Tag</DialogTitle>
            <DialogDescription className="font-body" style={{ color: "var(--admin-container-muted)" }}>
              Are you sure you want to delete <strong style={{ color: "var(--admin-container-text)" }}>{deleteTarget?.name}</strong>? This will also remove any logic rule attached to this tag. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} className="text-xs font-body"
              style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>Cancel</Button>
            <Button size="sm" onClick={handleDelete} disabled={deleting} className="text-xs font-body gap-1.5 bg-red-600 hover:bg-red-700 text-white"
              data-testid="metatag-confirm-delete-btn">
              {deleting && <Loader2 className="h-3 w-3 animate-spin" />} Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Rule Confirmation */}
      <Dialog open={!!ruleDeleteTarget} onOpenChange={() => setRuleDeleteTarget(null)}>
        <DialogContent style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: "var(--admin-container-text)" }}>Delete Logic Rule</DialogTitle>
            <DialogDescription className="font-body" style={{ color: "var(--admin-container-muted)" }}>
              Delete the rule for <strong style={{ color: "var(--admin-container-text)" }}>{ruleDeleteTarget?.tag_name}</strong>? Learners will no longer see these responses when they miss items tagged with it. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setRuleDeleteTarget(null)} className="text-xs font-body"
              style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}>Cancel</Button>
            <Button size="sm" onClick={handleRuleDelete} disabled={ruleDeleting} className="text-xs font-body gap-1.5 bg-red-600 hover:bg-red-700 text-white"
              data-testid="logic-rule-confirm-delete-btn">
              {ruleDeleting && <Loader2 className="h-3 w-3 animate-spin" />} Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
